import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Badge,
} from 'reactstrap';
import { diaryService } from '../services';
import { Tag } from '../types';
import BreadCrumb from '../components/Common/BreadCrumb';
import { toast } from 'react-toastify';

const TagsPage: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [form, setForm] = useState({ name: '', color: '#9CA3AF' });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; tagId: string }>({ show: false, tagId: '' });

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    setLoading(true);
    try {
      const data = await diaryService.getTags(false);
      setTags(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingTag(null);
    setForm({ name: '', color: '#9CA3AF' });
    setModalOpen(true);
  };

  const openEdit = (tag: Tag) => {
    setEditingTag(tag);
    setForm({ name: tag.name, color: tag.color || '#9CA3AF' });
    setModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Tag name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingTag) {
        await diaryService.updateTag(editingTag._id, form);
        toast.success('Tag updated');
      } else {
        await diaryService.createTag(form);
        toast.success('Tag created');
      }
      setModalOpen(false);
      loadTags();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save tag');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (id: string) => {
    setDeleteModal({ show: true, tagId: id });
  };

  const handleDelete = async () => {
    if (!deleteModal.tagId) return;
    setDeleteId(deleteModal.tagId);
    try {
      await diaryService.deleteTag(deleteModal.tagId);
      toast.success('Tag deleted');
      loadTags();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete tag');
    } finally {
      setDeleteId(null);
      setDeleteModal({ show: false, tagId: '' });
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Diary Tags" pageTitle="Diary Management" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">
                      <i className="ri-price-tag-3-line me-2"></i>
                      Tags
                    </h5>
                    <Button color="primary" onClick={openAdd}>
                      <i className="ri-add-line me-1"></i>
                      Add Tag
                    </Button>
                  </div>
                  <p className="text-muted small mb-4">
                    Manage tags used in diary entries (e.g. Gratitude, Inner Child).
                  </p>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading tags...</p>
                    </div>
                  ) : tags.length === 0 ? (
                    <div className="text-center py-5 border rounded bg-light">
                      <i className="ri-price-tag-3-line display-4 text-muted"></i>
                      <p className="mt-2 mb-2">No tags yet</p>
                      <Button color="primary" onClick={openAdd}>
                        <i className="ri-add-line me-1"></i>
                        Add First Tag
                      </Button>
                    </div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <div
                          key={tag._id}
                          className="d-flex align-items-center gap-2 rounded border p-2"
                          style={{ backgroundColor: '#f8f9fa' }}
                        >
                          <span
                            className="rounded-circle"
                            style={{
                              width: 14,
                              height: 14,
                              backgroundColor: tag.color || '#9CA3AF',
                            }}
                          />
                          <span className="fw-medium">{tag.name}</span>
                          {!tag.isActive && (
                            <Badge color="secondary">Inactive</Badge>
                          )}
                          <Button
                            color="light"
                            size="sm"
                            className="ms-1"
                            onClick={() => openEdit(tag)}
                          >
                            <i className="ri-edit-2-line"></i>
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            disabled={deleteId === tag._id}
                            onClick={() => openDeleteModal(tag._id)}
                          >
                            {deleteId === tag._id ? (
                              <Spinner size="sm" />
                            ) : (
                              <i className="ri-delete-bin-line"></i>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editingTag ? 'Edit Tag' : 'Add Tag'}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSave}>
            <FormGroup>
              <Label>Name <span className="text-danger">*</span></Label>
              <Input
                type="text"
                placeholder="e.g. Gratitude"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={50}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Color</Label>
              <div className="d-flex align-items-center gap-2">
                <Input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  style={{ width: 50, height: 38 }}
                />
                <Input
                  type="text"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  placeholder="#9CA3AF"
                />
              </div>
            </FormGroup>
            <div className="d-flex gap-2 justify-content-end mt-3">
              <Button type="button" color="light" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={saving}>
                {saving ? <Spinner size="sm" className="me-2" /> : null}
                {editingTag ? 'Update' : 'Create'}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      <Modal isOpen={deleteModal.show} toggle={() => setDeleteModal({ show: false, tagId: '' })} centered>
        <ModalHeader toggle={() => setDeleteModal({ show: false, tagId: '' })}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p className="mb-3">Are you sure you want to delete this tag?</p>
          <div className="d-flex justify-content-end gap-2">
            <Button
              type="button"
              color="light"
              onClick={() => setDeleteModal({ show: false, tagId: '' })}
              disabled={Boolean(deleteId)}
            >
              Cancel
            </Button>
            <Button type="button" color="danger" onClick={handleDelete} disabled={Boolean(deleteId)}>
              {deleteId ? <Spinner size="sm" className="me-2" /> : null}
              Delete
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default TagsPage;
